/////////////////////////////////////////////////////////////////
// src/pages/AdminPanel.jsx — v55
// CHANGED FROM v54 (5 Aug 2026): REAL FIX — this file imported `auth`
// directly from utils/firebase.js, which broke when firebase.js v2
// switched to deferred/async Auth loading (see that file's changelog
// — StoreContext.jsx was updated for this, but this file was missed,
// confirmed via local testing before any live push). Now imports and
// calls the async getFirebaseAuth() instead, inside callAdmin() where
// the token is actually needed. No other logic changed.
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { useStore } from '../context/StoreContext';
import { getFirebaseAuth } from '../utils/firebase';
import { getAdminNextAction, getItemStageDisplay } from '../utils/orderStages';
const ADMIN_EMAIL = 'nobrandstore3@gmail.com';
const MAJOR_COURIERS_RAW = [
  { name: 'Amazon Shipping', trackingUrlTemplate: 'https://track.amazon.in/tracking/{AWB}?trackingId={AWB}' },
  { name: 'Aramex', trackingUrlTemplate: 'https://www.aramex.com/in/en/track/results?source=aramex&ShipmentNumber={AWB}' },
  { name: 'DHL Express', trackingUrlTemplate: 'https://www.dhl.com/in-en/home/tracking.html?tracking-id={AWB}&submit=1&inputsource=marketingstage' },
  { name: 'Ekart', trackingUrlTemplate: 'https://ekartlogistics.com/ekartlogistics-web/shipmenttrack/{AWB}' },
  { name: 'FedEx', trackingUrlTemplate: 'https://www.fedex.com/wtrk/track/?action=track&tracknumbers={AWB}&locale=en_IN&cntry_code=us' },
  { name: 'TCI Express', trackingUrlTemplate: 'https://www.tciexpress.in/track-your-shipment?trackshipment={AWB}&dwb=dwb' },
  { name: 'Xpressbees', trackingUrlTemplate: 'https://www.xpressbees.com/shipment/tracking?awbNo={AWB}' },
];
const MAJOR_COURIERS = MAJOR_COURIERS_RAW;
function sortCouriers(list) {
  return [...list].sort((a, b) => a.name.localeCompare(b.name));
}
function adminItemStatusLabel(display) {
  if (display.key === 'preparing') return 'Not yet dispatched';
  return display.label;
}
function trackingSearchUrl(item, courierList) {
  if (!item.awb) return null;
  const match = (courierList || []).find(c => c.name === item.courierPartner);
  if (match && match.trackingUrlTemplate) {
    return match.trackingUrlTemplate.replace('{AWB}', encodeURIComponent(item.awb));
  }
  const q = encodeURIComponent(`${item.courierPartner || ''} tracking ${item.awb}`.trim());
  return `https://www.google.com/search?q=${q}`;
}
const NOBRANDSTORE_TOOLS_WEBAPP_URL = 'https://script.google.com/macros/s/AKfycbzzpmqK-aoMC1k5iiutMWG_28J8Cd-1fyYw40R8KEgathw-P6IeUvGQAOlNBHChuJzrqA/exec';
async function callProductsTool(action, payload) {
  if (!NOBRANDSTORE_TOOLS_WEBAPP_URL || NOBRANDSTORE_TOOLS_WEBAPP_URL.indexOf('PASTE_') === 0) {
    return { success: false, error: 'Products tool URL not configured yet — deploy the NobrandStore Tools web app and paste its URL into AdminPanel.jsx.' };
  }
  const res = await fetch(NOBRANDSTORE_TOOLS_WEBAPP_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain' },
    body: JSON.stringify({ action, ...payload }),
  });
  return res.json();
}
async function callAdmin(action, payload) {
  const { auth } = await getFirebaseAuth();
  const idToken = await auth.currentUser.getIdToken();
  const res = await fetch('/.netlify/functions/admin-actions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ idToken, action, payload }),
  });
  return res.json();
}
function formatDateTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}
function formatDateTimeWithSeconds(dateObj) {
  if (!dateObj) return '';
  return dateObj.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}
function nowStamp() {
  return new Date().toLocaleString('en-IN', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}
function csvEscape(val) {
  const s = val === null || val === undefined ? '' : String(val);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return '"' + s.replace(/"/g, '""') + '"';
  }
  return s;
}
export default function AdminPanel() {
  const { user, authLoading } = useStore();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [tab, setTab] = useState('dashboard');
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [openMenuOrderId, setOpenMenuOrderId] = useState(null);
  const [noteDrafts, setNoteDrafts] = useState({});
  const [busyKey, setBusyKey] = useState(null);
  const [msg, setMsg] = useState('');
  const [courierList, setCourierList] = useState(() => sortCouriers(MAJOR_COURIERS));
  const [courierAddedFlash, setCourierAddedFlash] = useState(null);
  const [walletCustomerId, setWalletCustomerId] = useState('');
  const [walletData, setWalletData] = useState(null);
  const [walletLoading, setWalletLoading] = useState(false);
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjustBusy, setAdjustBusy] = useState(false);
  const [lifecycleFilter, setLifecycleFilter] = useState(null);
  const [returnSubStage, setReturnSubStage] = useState(null);
  const [ordersLifecycleFilter, setOrdersLifecycleFilter] = useState(null);
  const [ordersReturnSubStage, setOrdersReturnSubStage] = useState(null);
  const [ordersFilterOrderId, setOrdersFilterOrderId] = useState('');
  const [lookupMode, setLookupMode] = useState(null);
  const [lookupQuery, setLookupQuery] = useState('');
  const [filterProdId, setFilterProdId] = useState('');
  const [filterOrderId, setFilterOrderId] = useState('');
  const [filterCustomer, setFilterCustomer] = useState('');
  const [auditSearch, setAuditSearch] = useState('');
  const [showFullHistory, setShowFullHistory] = useState(false);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('desc');
  const [copiedRowIdx, setCopiedRowIdx] = useState(null);
  const [colWidths, setColWidths] = useState({ ts: 150, order: 150, prod: 100, name: 110, phone: 100, action: 260, by: 70 });
  const resizingRef = React.useRef(null);
  function startResize(colKey, e) {
    resizingRef.current = { colKey, startX: e.clientX, startWidth: colWidths[colKey] };
    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', stopResize);
  }
  function handleResizeMove(e) {
    if (!resizingRef.current) return;
    const { colKey, startX, startWidth } = resizingRef.current;
    const newWidth = Math.max(50, startWidth + (e.clientX - startX));
    setColWidths(prev => ({ ...prev, [colKey]: newWidth }));
  }
  function stopResize() {
    resizingRef.current = null;
    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', stopResize);
  }
  const dashGridTemplate = `${colWidths.ts}px ${colWidths.order}px ${colWidths.prod}px ${colWidths.name}px ${colWidths.phone}px minmax(120px, ${colWidths.action}px) ${colWidths.by}px 32px`;
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [productSearchBusy, setProductSearchBusy] = useState(false);
  const [productUpdateBusyId, setProductUpdateBusyId] = useState(null);
  const [dispatchModal, setDispatchModal] = useState(null);
  const [dispatchAwb, setDispatchAwb] = useState('');
  const [dispatchCourierChoice, setDispatchCourierChoice] = useState('');
  const [dispatchCourierOther, setDispatchCourierOther] = useState('');
  const [dispatchCourierUrl, setDispatchCourierUrl] = useState('');
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectNote, setRejectNote] = useState('');
  const [scheduleModal, setScheduleModal] = useState(null);
  const [scheduleAwb, setScheduleAwb] = useState('');
  const [scheduleCourierChoice, setScheduleCourierChoice] = useState('');
  const [scheduleCourierOther, setScheduleCourierOther] = useState('');
  const [scheduleCourierUrl, setScheduleCourierUrl] = useState('');
  const [manualDeliveryModal, setManualDeliveryModal] = useState(null);
  const [manualDeliveryDate, setManualDeliveryDate] = useState('');
  const [manualDeliveryHour, setManualDeliveryHour] = useState('');
  const [manualDeliveryMinute, setManualDeliveryMinute] = useState('');
  const [manualDeliveryAmPm, setManualDeliveryAmPm] = useState('PM');
  const [showUnackList, setShowUnackList] = useState(false);
  useEffect(() => {
    if (authLoading) return;
    if (!user || user.email !== ADMIN_EMAIL) {
      navigate('/');
    }
  }, [user, authLoading, navigate]);
  useEffect(() => {
    if (user && user.email === ADMIN_EMAIL) {
      loadOrders();
      loadCouriers();
    }
  }, [user]);
  async function loadOrders() {
    setOrdersLoading(true);
    const result = await callAdmin('get_all_orders', {});
    if (result.success) setOrders(result.orders);
    setOrdersLoading(false);
  }
  async function loadCouriers() {
    const result = await callAdmin('get_couriers_list', {});
    if (result.success && result.couriers) {
      const byName = {};
      MAJOR_COURIERS.forEach(c => { byName[c.name] = c; });
      result.couriers.forEach(c => { byName[c.name] = c; });
      setCourierList(sortCouriers(Object.values(byName)));
    }
  }
  async function handleAddCourier(name, urlTemplate, applyTo) {
    const trimmed = (name || '').trim();
    if (!trimmed) return;
    const result = await callAdmin('add_courier', { name: trimmed, trackingUrlTemplate: (urlTemplate || '').trim() });
    if (result.success) {
      setCourierList(sortCouriers(result.couriers));
      setCourierAddedFlash(applyTo);
      setTimeout(() => {
        setCourierAddedFlash(null);
        if (applyTo === 'dispatch') {
          setDispatchCourierChoice(trimmed);
          setDispatchCourierOther('');
          setDispatchCourierUrl('');
        } else if (applyTo === 'schedule') {
          setScheduleCourierChoice(trimmed);
          setScheduleCourierOther('');
          setScheduleCourierUrl('');
        }
      }, 1500);
    } else {
      setMsg(`Error: ${result.error}`);
    }
  }
  function itemKeyFor(orderId, productId) {
    return orderId + '|' + productId;
  }
  function openDispatchModal(orderId, productId, lineKey) {
    setDispatchAwb('');
    setDispatchCourierChoice('');
    setDispatchCourierOther('');
    setDispatchCourierUrl('');
    setDispatchModal({ orderId, productId, lineKey });
  }
  async function submitDispatch() {
    const finalCourier = dispatchCourierChoice === 'Others' ? dispatchCourierOther.trim() : dispatchCourierChoice;
    if (!dispatchAwb.trim() || !dispatchCourierChoice || (dispatchCourierChoice === 'Others' && !dispatchCourierOther.trim())) {
      setMsg('Please enter the AWB number and select (or enter) a courier partner.');
      return;
    }
    const { orderId, productId, lineKey } = dispatchModal;
    const key = itemKeyFor(orderId, productId);
    setBusyKey(key);
    const result = await callAdmin('admin_mark_dispatched', { orderId, productId, awb: dispatchAwb.trim(), courier: finalCourier, lineKey });
    setBusyKey(null);
    setDispatchModal(null);
    if (result.success) {
      setMsg(`${productId} (order ${orderId}) marked dispatched via ${finalCourier}. Customer notified via WhatsApp. · ${nowStamp()}`);
      loadOrders();
    } else {
      setMsg(`Error: ${result.error}`);
    }
  }
  function openRejectModal(orderId, productId, lineKey) {
    setRejectNote('');
    setRejectModal({ orderId, productId, lineKey });
  }
  async function submitReject() {
    if (!rejectNote.trim()) {
      setMsg('Please enter a note explaining why this return is being rejected.');
      return;
    }
    const { orderId, productId, lineKey } = rejectModal;
    const key = itemKeyFor(orderId, productId);
    setBusyKey(key);
    const result = await callAdmin('admin_reject_return', { orderId, productId, note: rejectNote.trim(), lineKey });
    setBusyKey(null);
    setRejectModal(null);
    if (result.success) {
      setMsg(`Return rejected for ${productId} in ${orderId}. This item's cashback is unblocked. · ${nowStamp()}`);
      loadOrders();
    } else {
      setMsg(`Error: ${result.error}`);
    }
  }
  async function approveReturn(orderId, productId, lineKey) {
    const key = itemKeyFor(orderId, productId);
    setBusyKey(key);
    const result = await callAdmin('admin_approve_return', { orderId, productId, lineKey });
    setBusyKey(null);
    if (result.success) {
      setMsg(`Return approved for ${productId} in ${orderId}. · ${nowStamp()}`);
      loadOrders();
    } else {
      setMsg(`Error: ${result.error}`);
    }
  }
  function openScheduleModal(orderId, productId, lineKey) {
    setScheduleAwb('');
    setScheduleCourierChoice('');
    setScheduleCourierOther('');
    setScheduleCourierUrl('');
    setScheduleModal({ orderId, productId, lineKey });
  }
  async function submitSchedule() {
    const finalCourier = scheduleCourierChoice === 'Others' ? scheduleCourierOther.trim() : scheduleCourierChoice;
    if (!scheduleAwb.trim() || !scheduleCourierChoice || (scheduleCourierChoice === 'Others' && !scheduleCourierOther.trim())) {
      setMsg('Please enter the return pickup AWB and select (or enter) a courier partner.');
      return;
    }
    const { orderId, productId, lineKey } = scheduleModal;
    const key = itemKeyFor(orderId, productId);
    setBusyKey(key);
    const result = await callAdmin('admin_mark_return_scheduled', {
      orderId, productId, returnAwb: scheduleAwb.trim(), returnCourier: finalCourier, lineKey,
    });
    setBusyKey(null);
    setScheduleModal(null);
    if (result.success) {
      setMsg(`${productId} in ${orderId} marked as pickup scheduled. · ${nowStamp()}`);
      loadOrders();
    } else {
      setMsg(`Error: ${result.error}`);
    }
  }
  async function markReturnPicked(orderId, productId, lineKey) {
    const key = itemKeyFor(orderId, productId);
    setBusyKey(key);
    const result = await callAdmin('admin_mark_return_picked', { orderId, productId, lineKey });
    setBusyKey(null);
    if (result.success) {
      setMsg(`${productId} in ${orderId} marked as picked up. · ${nowStamp()}`);
      loadOrders();
    } else {
      setMsg(`Error: ${result.error}`);
    }
  }
  async function markReturnReceived(orderId, productId, lineKey) {
    const key = itemKeyFor(orderId, productId);
    setBusyKey(key);
    const result = await callAdmin('admin_mark_return_received', { orderId, productId, lineKey });
    setBusyKey(null);
    if (result.success) {
      setMsg(`${productId} in ${orderId} marked as received. Customer can now choose refund or wallet. · ${nowStamp()}`);
      loadOrders();
    } else {
      setMsg(`Error: ${result.error}`);
    }
  }
  async function markOutOfStock(orderId) {
    if (!window.confirm(`Mark ${orderId} as Out of Stock? This unlocks the refund/wallet choice for the customer immediately.`)) return;
    setBusyKey(orderId);
    const result = await callAdmin('admin_mark_out_of_stock', { orderId });
    setBusyKey(null);
    if (result.success) {
      setMsg(`Order ${orderId} marked Out of Stock. Customer can now choose refund or wallet. · ${nowStamp()}`);
      loadOrders();
    } else {
      setMsg(`Error: ${result.error}`);
    }
  }
  async function sendDeliveryReminder(orderId) {
    setBusyKey(orderId);
    const result = await callAdmin('admin_send_delivery_reminder', { orderId });
    setBusyKey(null);
    if (result.success) {
      setMsg(`Delivery reminder sent for ${orderId}. · ${nowStamp()}`);
      loadOrders();
    } else {
      setMsg(`Error: ${result.error}`);
    }
  }
  async function flagDeliveryIssue(orderId) {
    const note = noteDrafts[orderId] || '';
    if (!note.trim()) {
      setMsg('Please enter a note before flagging.');
      return;
    }
    setBusyKey(orderId);
    const result = await callAdmin('admin_flag_delivery_issue', { orderId, note });
    setBusyKey(null);
    if (result.success) {
      setMsg(`Delivery issue flagged for ${orderId}. · ${nowStamp()}`);
      loadOrders();
    } else {
      setMsg(`Error: ${result.error}`);
    }
  }
  async function lookupWallet() {
    if (!walletCustomerId.trim()) return;
    setWalletLoading(true);
    setWalletData(null);
    const result = await callAdmin('get_wallet_summary', { customerId: walletCustomerId.trim() });
    setWalletLoading(false);
    if (result.success) {
      setWalletData(result.summary);
    } else {
      setMsg(`Error: ${result.error || 'Could not fetch wallet'}`);
    }
  }
  async function submitAdjustment() {
    const amount = Number(adjustAmount);
    if (!amount || amount === 0) {
      setMsg('Enter a non-zero amount (positive to add, negative to deduct).');
      return;
    }
    if (!adjustReason.trim()) {
      setMsg('Please enter a reason for this adjustment.');
      return;
    }
    setAdjustBusy(true);
    const result = await callAdmin('admin_wallet_adjustment', {
      customerId: walletCustomerId.trim(),
      amount,
      reason: adjustReason.trim(),
    });
    setAdjustBusy(false);
    if (result.success) {
      setMsg(`Wallet updated. New balance: ₹${result.newBalance} · ${nowStamp()}`);
      setAdjustAmount('');
      setAdjustReason('');
      lookupWallet();
    } else {
      setMsg(`Error: ${result.error}`);
    }
  }
  function openManualDeliveryModal(orderId, productId, lineKey) {
    setManualDeliveryDate('');
    setManualDeliveryHour('');
    setManualDeliveryMinute('');
    setManualDeliveryAmPm('PM');
    setManualDeliveryModal({ orderId, productId, lineKey });
  }
  async function submitManualDelivery() {
    if (!manualDeliveryDate || !manualDeliveryHour || !manualDeliveryMinute) {
      setMsg('Please enter the real delivery date and time.');
      return;
    }
    let h24 = parseInt(manualDeliveryHour, 10);
    if (manualDeliveryAmPm === 'PM' && h24 !== 12) h24 += 12;
    if (manualDeliveryAmPm === 'AM' && h24 === 12) h24 = 0;
    const deliveryDateTime = `${manualDeliveryDate}T${String(h24).padStart(2, '0')}:${manualDeliveryMinute}:00`;
    const { orderId, productId, lineKey } = manualDeliveryModal;
    const key = itemKeyFor(orderId, productId);
    setBusyKey(key);
    const result = await callAdmin('admin_manual_confirm_delivery', { orderId, productId, lineKey, deliveryDateTime });
    setBusyKey(null);
    setManualDeliveryModal(null);
    if (result.success) {
      setMsg(`${productId} in ${orderId} confirmed delivered. Return window and cashback timer now anchored to that real date. · ${nowStamp()}`);
      loadOrders();
    } else {
      setMsg(`Error: ${result.error}`);
    }
  }
  function itemsSummary(o) {
    return (o.items || []).map(it => it.itemDescriptor).join(' | ');
  }
  async function searchProducts() {
    if (!productSearchQuery.trim()) { setProductResults([]); return; }
    setProductSearchBusy(true);
    const result = await callProductsTool('search_products', { query: productSearchQuery.trim() });
    setProductSearchBusy(false);
    if (result.success) {
      setProductResults(result.products || []);
    } else {
      setMsg(`Error: ${result.error}`);
    }
  }
  async function updateProductStatus(prodId, status) {
    setProductUpdateBusyId(prodId);
    const result = await callProductsTool('update_status', { prodId, status });
    setProductUpdateBusyId(null);
    if (result.success) {
      setProductResults(prev => prev.map(p => p.prodId === prodId ? { ...p, status } : p));
      setMsg(`${prodId} marked ${status}. · ${nowStamp()}`);
    } else {
      setMsg(`Error: ${result.error}`);
    }
  }
  const dashboardMetrics = useMemo(() => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfWeek = new Date(startOfToday);
    startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    let todaysOrders = 0, pendingReturns = 0, revenueWeek = 0, revenueMonth = 0;
    orders.forEach(o => {
      const ts = new Date(o.timestamp);
      if (ts >= startOfToday) todaysOrders++;
      if (o.status === 'Paid') {
        if (ts >= startOfWeek) revenueWeek += Number(o.orderTotal) || 0;
        if (ts >= startOfMonth) revenueMonth += Number(o.orderTotal) || 0;
      }
      (o.items || []).forEach(it => {
        if (['Requested', 'Approved', 'Scheduled', 'Picked'].includes(it.returnStage)) pendingReturns++;
      });
    });
    return { todaysOrders, pendingReturns, revenueWeek, revenueMonth };
  }, [orders]);
  const LIFECYCLE_BUCKETS = [
    { key: 'pending_dispatch', label: 'Pending Dispatch' },
    { key: 'in_transit', label: 'In Transit' },
    { key: 'cashback_pending', label: 'Cashback Pending' },
    { key: 'return_progress', label: 'Returns' },
    { key: 'cashback_earned', label: 'Cashback Earned' },
  ];
  const RETURN_SUB_STAGES = ['Requested', 'Approved', 'Rejected', 'Scheduled', 'Picked', 'Received', 'Processed'];
  function bucketForStageKey(key) {
    if (key === 'preparing') return 'pending_dispatch';
    if (key === 'in_transit') return 'in_transit';
    if (key === 'return_window_open' || key === 'return_window_closed') return 'cashback_pending';
    if (key === 'cashback_credited') return 'cashback_earned';
    if (key === 'refund_choice_made') return 'return_progress';
    if (['return_requested', 'return_rejected', 'return_approved', 'return_scheduled', 'return_picked', 'awaiting_choice'].includes(key)) return 'return_progress';
    return null;
  }
  function subStageForKey(key) {
    const map = { return_requested: 'Requested', return_approved: 'Approved', return_rejected: 'Rejected', return_scheduled: 'Scheduled', return_picked: 'Picked', awaiting_choice: 'Received', refund_choice_made: 'Processed' };
    return map[key] || null;
  }
  const itemStages = useMemo(() => {
    const rows = [];
    orders.forEach(o => {
      (o.items || []).forEach(it => {
        const display = getItemStageDisplay(it);
        rows.push({ orderId: o.orderId, prodId: it.productId, bucket: bucketForStageKey(display.key), subStage: subStageForKey(display.key) });
      });
    });
    return rows;
  }, [orders]);
  const bucketCounts = useMemo(() => {
    const counts = {};
    LIFECYCLE_BUCKETS.forEach(b => { counts[b.key] = new Set(); });
    itemStages.forEach(r => { if (r.bucket) counts[r.bucket].add(r.orderId); });
    const out = {};
    Object.keys(counts).forEach(k => { out[k] = counts[k].size; });
    return out;
  }, [itemStages]);
  const subStageCounts = useMemo(() => {
    const counts = {};
    RETURN_SUB_STAGES.forEach(s => { counts[s] = new Set(); });
    itemStages.forEach(r => { if (r.bucket === 'return_progress' && r.subStage) counts[r.subStage].add(r.orderId); });
    const out = {};
    Object.keys(counts).forEach(k => { out[k] = counts[k].size; });
    return out;
  }, [itemStages]);
  const pillOrderIds = useMemo(() => {
    if (!lifecycleFilter) return [];
    let rows = itemStages.filter(r => r.bucket === lifecycleFilter);
    if (lifecycleFilter === 'return_progress' && returnSubStage) {
      rows = rows.filter(r => r.subStage === returnSubStage);
    }
    return Array.from(new Set(rows.map(r => r.orderId)));
  }, [itemStages, lifecycleFilter, returnSubStage]);
  const ordersPillOrderIds = useMemo(() => {
    if (!ordersLifecycleFilter) return [];
    let rows = itemStages.filter(r => r.bucket === ordersLifecycleFilter);
    if (ordersLifecycleFilter === 'return_progress' && ordersReturnSubStage) {
      rows = rows.filter(r => r.subStage === ordersReturnSubStage);
    }
    return Array.from(new Set(rows.map(r => r.orderId)));
  }, [itemStages, ordersLifecycleFilter, ordersReturnSubStage]);
  function parseActivityTimestamp(tsText) {
    if (!tsText) return null;
    const parsed = Date.parse(tsText);
    if (isNaN(parsed)) return null;
    const d = new Date(parsed);
    if (d.getFullYear() < 2020) {
      const now = new Date();
      d.setFullYear(now.getFullYear());
      if (d > now) d.setFullYear(now.getFullYear() - 1);
    }
    return d;
  }
  const auditEntries = useMemo(() => {
    const rows = [];
    let seq = 0;
    orders.forEach(o => {
      (o.items || []).forEach(it => {
        const a = it.activity;
        if (!a) return;
        const prodId = it.productId;
        if (a.orderBooked) {
          rows.push({ date: new Date(a.orderBooked), seq: seq++, orderId: o.orderId, prodId, action: 'New order received', by: 'Customer', isNewOrder: true, isReturn: false, isWhatsApp: false, customerName: o.customerName, customerPhone: o.customerPhone });
        }
        const fields = [
          { cell: a.dispatched, by: 'Admin' },
          { cell: a.deliveryReminderSent, by: 'Admin' },
          { cell: a.parcelReceived, by: 'Customer' },
          { cell: a.returnRequested, by: 'Customer', isReturn: true },
          { cell: a.returnApprovedRejected, by: 'Admin', isReturn: true },
          { cell: a.returnScheduled, by: 'Admin', isReturn: true },
          { cell: a.returnPicked, by: 'Admin', isReturn: true },
          { cell: a.returnReceived, by: 'Admin', isReturn: true },
          { cell: a.refundWalletChoice, by: 'Customer' },
          { cell: a.walletCreditedRefund, by: 'Admin' },
          { cell: a.outOfStock, by: 'Admin' },
        ];
        fields.forEach(f => {
          if (!f.cell) return;
          const sepIdx = f.cell.indexOf(' — ');
          const tsText = sepIdx > -1 ? f.cell.slice(0, sepIdx) : '';
          const detail = sepIdx > -1 ? f.cell.slice(sepIdx + 3) : f.cell;
          const parsedDate = parseActivityTimestamp(tsText);
          const isAuto = /\(auto\)/i.test(detail);
          rows.push({
            date: parsedDate || new Date(0),
            seq: seq++,
            orderId: o.orderId, prodId, action: detail,
            by: isAuto ? 'System' : f.by,
            isNewOrder: false, isReturn: !!f.isReturn, isWhatsApp: false,
            customerName: o.customerName, customerPhone: o.customerPhone,
          });
        });
        (it.whatsappSends || []).forEach(w => {
          rows.push({
            date: w.timestamp ? new Date(w.timestamp) : new Date(0),
            seq: seq++,
            orderId: o.orderId, prodId, action: 'WhatsApp: ' + w.templateName,
            by: 'System',
            isNewOrder: false, isReturn: false, isWhatsApp: true,
            customerName: o.customerName, customerPhone: o.customerPhone,
          });
        });
      });
      (o.whatsappOrderLevelSends || []).forEach(w => {
        rows.push({
          date: w.timestamp ? new Date(w.timestamp) : new Date(0),
          seq: seq++,
          orderId: o.orderId, prodId: '—', action: 'WhatsApp: ' + w.templateName,
          by: 'System',
          isNewOrder: false, isReturn: false, isWhatsApp: true,
          customerName: o.customerName, customerPhone: o.customerPhone,
        });
      });
    });
    rows.sort((x, y) => (y.date - x.date) || (y.seq - x.seq));
    return rows;
  }, [orders]);
  const fifteenDaysAgo = useMemo(() => { const d = new Date(); d.setDate(d.getDate() - 15); return d; }, []);
  const tenDaysAgo = useMemo(() => { const d = new Date(); d.setDate(d.getDate() - 10); return d; }, []);
  const unacknowledgedParcels = useMemo(() => {
    const rows = [];
    orders.forEach(o => {
      (o.items || []).forEach(it => {
        if (!it.dispatchedDate || it.parcelReceivedTime) return;
        const dispatched = new Date(it.dispatchedDate);
        if (dispatched <= tenDaysAgo) {
          rows.push({ orderId: o.orderId, productId: it.productId, lineKey: it.lineKey, dispatchedDate: it.dispatchedDate, customerName: o.customerName });
        }
      });
    });
    return rows;
  }, [orders, tenDaysAgo]);
  const hasActiveFilter = !!(filterProdId || filterOrderId || filterCustomer || auditSearch.trim() || lifecycleFilter);
  const filteredAuditEntries = useMemo(() => {
    let list = auditEntries;
    if (!showFullHistory && !hasActiveFilter) {
      list = list.filter(r => r.date >= fifteenDaysAgo);
    }
    if (lifecycleFilter) {
      const bucketOrderIds = new Set(pillOrderIds);
      list = list.filter(r => bucketOrderIds.has(r.orderId));
    }
    if (filterProdId) list = list.filter(r => r.prodId === filterProdId);
    if (filterOrderId) list = list.filter(r => r.orderId === filterOrderId);
    if (filterCustomer) {
      const matchingOrderIds = new Set(orders.filter(o => o.customerName === filterCustomer || o.customerPhone === filterCustomer).map(o => o.orderId));
      list = list.filter(r => matchingOrderIds.has(r.orderId));
    }
    if (auditSearch.trim()) {
      const q = auditSearch.trim().toLowerCase();
      list = list.filter(r =>
        r.orderId.toLowerCase().includes(q) ||
        (r.prodId || '').toLowerCase().includes(q) ||
        r.action.toLowerCase().includes(q)
      );
    }
    if (sortKey) {
      list = [...list].sort((a, b) => {
        let av, bv;
        if (sortKey === 'date') {
          const cmp = (a.date - b.date) || (a.seq - b.seq);
          return sortDir === 'asc' ? cmp : -cmp;
        }
        else if (sortKey === 'orderId') { av = a.orderId; bv = b.orderId; }
        else if (sortKey === 'prodId') { av = a.prodId || ''; bv = b.prodId || ''; }
        else if (sortKey === 'customerName') { av = a.customerName || ''; bv = b.customerName || ''; }
        else if (sortKey === 'customerPhone') { av = a.customerPhone || ''; bv = b.customerPhone || ''; }
        else if (sortKey === 'action') { av = a.action; bv = b.action; }
        if (av < bv) return sortDir === 'asc' ? -1 : 1;
        if (av > bv) return sortDir === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return list;
  }, [auditEntries, showFullHistory, hasActiveFilter, lifecycleFilter, pillOrderIds, filterProdId, filterOrderId, filterCustomer, auditSearch, fifteenDaysAgo, orders, sortKey, sortDir]);
  function toggleSort(key) {
    if (sortKey === key) {
      if (sortDir === 'desc') setSortDir('asc');
      else { setSortKey(null); setSortDir('desc'); }
    } else {
      setSortKey(key); setSortDir('desc');
    }
  }
  const lookupOptions = useMemo(() => {
    if (!lookupMode) return [];
    const set = new Set();
    orders.forEach(o => {
      if (lookupMode === 'prodid' && filterOrderId && o.orderId !== filterOrderId) return;
      if (lookupMode === 'name' && o.customerName) set.add(o.customerName);
      if (lookupMode === 'phone' && o.customerPhone) set.add(o.customerPhone);
      if (lookupMode === 'orderid') set.add(o.orderId);
      if (lookupMode === 'prodid') (o.items || []).forEach(it => set.add(it.productId));
    });
    return Array.from(set);
  }, [orders, lookupMode, filterOrderId]);
  const lookupFiltered = useMemo(() => {
    const q = lookupQuery.trim().toLowerCase();
    const list = q ? lookupOptions.filter(v => String(v).toLowerCase().includes(q)) : lookupOptions;
    return list.slice(0, 20);
  }, [lookupOptions, lookupQuery]);
  function applyLookupSelection(value) {
    if (lookupMode === 'prodid') setFilterProdId(value);
    if (lookupMode === 'orderid') setFilterOrderId(value);
    if (lookupMode === 'name' || lookupMode === 'phone') setFilterCustomer(value);
    setLookupMode(null);
    setLookupQuery('');
  }
  function clearAllFilters() {
    setFilterProdId(''); setFilterOrderId(''); setFilterCustomer('');
    setAuditSearch(''); setLifecycleFilter(null); setReturnSubStage(null);
  }
  function copyAuditRow(row, idx) {
    const text = `${formatDateTimeWithSeconds(row.date)} — [${row.prodId}] ${row.action} (Order ${row.orderId}, by ${row.by})`;
    navigator.clipboard.writeText(text);
    setCopiedRowIdx(idx);
    setTimeout(() => setCopiedRowIdx(prev => (prev === idx ? null : prev)), 1500);
  }
  const filteredOrders = orders.filter(o => {
    if (ordersLifecycleFilter && !ordersPillOrderIds.includes(o.orderId)) return false;
    if (ordersFilterOrderId && o.orderId !== ordersFilterOrderId) return false;
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      const matches = (
        String(o.orderId || '').toLowerCase().includes(s) ||
        String(o.customerId || '').toLowerCase().includes(s) ||
        String(o.customerPhone || '').toLowerCase().includes(s) ||
        String(o.customerName || '').toLowerCase().includes(s)
      );
      if (!matches) return false;
    }
    if (productSearch.trim()) {
      const p = productSearch.trim().toLowerCase();
      if (!itemsSummary(o).toLowerCase().includes(p)) return false;
    }
    if (dateFrom) {
      const from = new Date(dateFrom + 'T00:00:00');
      if (new Date(o.timestamp) < from) return false;
    }
    if (dateTo) {
      const to = new Date(dateTo + 'T23:59:59');
      if (new Date(o.timestamp) > to) return false;
    }
    return true;
  });
  function exportCSV() {
    const cols = [
      'Order ID', 'Customer ID', 'Customer Name', 'Phone', 'Items', 'Item Count', 'Order Total',
      'Status', 'Order Status', 'Timestamp', 'AWB List', 'Courier List',
      'Return Stages', 'Return Reasons', 'Refund/Wallet Choices', 'Wallet Used', 'Refund Status',
    ];
    const rows = filteredOrders.map(o => [
      o.orderId, o.customerId, o.customerName, o.customerPhone, itemsSummary(o), (o.items || []).length, o.orderTotal,
      o.status, o.orderStatus, o.timestamp,
      (o.items || []).map(it => it.awb).filter(Boolean).join(' | '),
      (o.items || []).map(it => it.courierPartner).filter(Boolean).join(' | '),
      (o.items || []).map(it => it.returnStage).filter(Boolean).join(' | '),
      (o.items || []).map(it => it.returnReason).filter(Boolean).join(' | '),
      (o.items || []).map(it => it.refundOrWalletChoice).filter(Boolean).join(' | '),
      o.walletUsed, o.refundStatus,
    ]);
    const csv = [cols.join(','), ...rows.map(r => r.map(csvEscape).join(','))].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `nobrandstore_orders_${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
  if (authLoading || !user || user.email !== ADMIN_EMAIL) return null;
  return (
    <div className="app-shell notranslate" translate="no">
      <Navbar showBack title="Admin Panel" onBack={() => navigate('/')} />
      <div className="admin-shell">
        <div className={`admin-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`} style={{ width: sidebarCollapsed ? 52 : 160 }}>
          <div className="sidebar-header">
            {!sidebarCollapsed && <span className="sidebar-title">Menu</span>}
            <button
              onClick={() => setSidebarCollapsed(v => !v)}
              aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              className="sidebar-toggle"
            >
              <i className={`ti ${sidebarCollapsed ? 'ti-chevrons-right' : 'ti-chevrons-left'}`}></i>
            </button>
          </div>
          <div className="sidebar-buttons">
            <button
              onClick={() => setTab('dashboard')}
              className="sidebar-btn"
              style={{ background: tab === 'dashboard' ? 'var(--navy)' : 'none', color: tab === 'dashboard' ? '#fff' : 'var(--navy)' }}
            >
              <i className="ti ti-chart-bar" style={{ fontSize: 18 }}></i>
              {!sidebarCollapsed && <span>Dashboard</span>}
            </button>
            <button
              onClick={() => setTab('orders')}
              className="sidebar-btn"
              style={{ background: tab === 'orders' ? 'var(--navy)' : 'none', color: tab === 'orders' ? '#fff' : 'var(--navy)' }}
            >
              <i className="ti ti-list-details" style={{ fontSize: 18 }}></i>
              {!sidebarCollapsed && <span>Orders</span>}
            </button>
            <button
              onClick={() => setTab('wallet')}
              className="sidebar-btn"
              style={{ background: tab === 'wallet' ? 'var(--navy)' : 'none', color: tab === 'wallet' ? '#fff' : 'var(--navy)' }}
            >
              <i className="ti ti-wallet" style={{ fontSize: 18 }}></i>
              {!sidebarCollapsed && <span>Wallet</span>}
            </button>
            <button
              onClick={() => setTab('products')}
              className="sidebar-btn"
              style={{ background: tab === 'products' ? 'var(--navy)' : 'none', color: tab === 'products' ? '#fff' : 'var(--navy)' }}
            >
              <i className="ti ti-package" style={{ fontSize: 18 }}></i>
              {!sidebarCollapsed && <span>Products</span>}
            </button>
          </div>
        </div>
        <div className="admin-content">
          {dispatchModal && (
            <div
              onClick={() => setDispatchModal(null)}
              style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}
            >
              <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, padding: '1.25rem', width: '100%', maxWidth: 340 }}>
                <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--navy)', marginBottom: 12 }}>Mark Dispatched — {dispatchModal.productId}</p>
                <input
                  type="text" placeholder="AWB / tracking number" value={dispatchAwb}
                  onChange={e => setDispatchAwb(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', border: '0.5px solid rgba(27,42,74,0.2)', borderRadius: 6, fontSize: 13, marginBottom: 10, fontFamily: 'var(--font)' }}
                />
                <select
                  value={dispatchCourierChoice}
                  onChange={e => setDispatchCourierChoice(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', border: '0.5px solid rgba(27,42,74,0.2)', borderRadius: 6, fontSize: 13, marginBottom: dispatchCourierChoice === 'Others' ? 10 : 14, fontFamily: 'var(--font)', background: '#fff' }}
                >
                  <option value="">Select courier partner</option>
                  {courierList.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                  <option value="Others">Others</option>
                </select>
                {dispatchCourierChoice === 'Others' && (
                  <div style={{ marginBottom: 14 }}>
                    <input
                      type="text" placeholder="Courier partner name" value={dispatchCourierOther}
                      onChange={e => setDispatchCourierOther(e.target.value)}
                      style={{ width: '100%', padding: '10px 12px', border: '0.5px solid rgba(27,42,74,0.2)', borderRadius: 6, fontSize: 13, marginBottom: 8, fontFamily: 'var(--font)' }}
                    />
                    <input
                      type="text" placeholder="Tracking URL — put {AWB} where the number goes, e.g. https://x.com/track?id={AWB}" value={dispatchCourierUrl}
                      onChange={e => setDispatchCourierUrl(e.target.value)}
                      style={{ width: '100%', padding: '10px 12px', border: '0.5px solid rgba(27,42,74,0.2)', borderRadius: 6, fontSize: 13, marginBottom: 4, fontFamily: 'var(--font)' }}
                    />
                    <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 8 }}>
                      Optional — leave blank to use a generic search-based tracking link for now instead.
                    </div>
                    <button
                      type="button"
                      onClick={() => handleAddCourier(dispatchCourierOther, dispatchCourierUrl, 'dispatch')}
                      disabled={!dispatchCourierOther.trim() || courierAddedFlash === 'dispatch'}
                      style={{ width: '100%', padding: 10, background: courierAddedFlash === 'dispatch' ? '#1B7A3D' : (dispatchCourierOther.trim() ? 'var(--blue)' : '#ccc'), color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: dispatchCourierOther.trim() ? 'pointer' : 'not-allowed', fontFamily: 'var(--font)' }}
                    >
                      {courierAddedFlash === 'dispatch' ? 'Partner added ✓' : '+ Add to courier list'}
                    </button>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setDispatchModal(null)} style={{ flex: 1, padding: '10px', background: '#eee', color: 'var(--navy)', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Cancel</button>
                  <button onClick={submitDispatch} disabled={busyKey === itemKeyFor(dispatchModal.orderId, dispatchModal.productId)} style={{ flex: 1, padding: '10px', background: 'var(--blue)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
                    {busyKey === itemKeyFor(dispatchModal.orderId, dispatchModal.productId) ? 'Saving…' : 'Confirm Dispatch'}
                  </button>
                </div>
              </div>
            </div>
          )}
          {rejectModal && (
            <div
              onClick={() => setRejectModal(null)}
              style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}
            >
              <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, padding: '1.25rem', width: '100%', maxWidth: 340 }}>
                <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--navy)', marginBottom: 4 }}>Reject Return — {rejectModal.productId}</p>
                <p style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 12 }}>This unblocks the item's cashback so it can process normally. No refund or wallet credit happens here.</p>
                <textarea
                  placeholder="Note — why this return is being rejected (required)" value={rejectNote}
                  onChange={e => setRejectNote(e.target.value)} rows={3}
                  style={{ width: '100%', padding: '10px 12px', border: '0.5px solid rgba(27,42,74,0.2)', borderRadius: 6, fontSize: 13, marginBottom: 14, fontFamily: 'var(--font)', resize: 'vertical' }}
                />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setRejectModal(null)} style={{ flex: 1, padding: '10px', background: '#eee', color: 'var(--navy)', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Cancel</button>
                  <button onClick={submitReject} disabled={busyKey === itemKeyFor(rejectModal.orderId, rejectModal.productId)} style={{ flex: 1, padding: '10px', background: '#D64545', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
                    {busyKey === itemKeyFor(rejectModal.orderId, rejectModal.productId) ? 'Saving…' : 'Confirm Reject'}
                  </button>
                </div>
              </div>
            </div>
          )}
          {scheduleModal && (
            <div
              onClick={() => setScheduleModal(null)}
              style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}
            >
              <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, padding: '1.25rem', width: '100%', maxWidth: 340 }}>
                <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--navy)', marginBottom: 4 }}>Mark Return Scheduled — {scheduleModal.productId}</p>
                <p style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 12 }}>Enter the return pickup's own AWB and courier — this is separate from the original outbound dispatch details.</p>
                <input
                  type="text" placeholder="Return pickup AWB / tracking number" value={scheduleAwb}
                  onChange={e => setScheduleAwb(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', border: '0.5px solid rgba(27,42,74,0.2)', borderRadius: 6, fontSize: 13, marginBottom: 10, fontFamily: 'var(--font)' }}
                />
                <select
                  value={scheduleCourierChoice}
                  onChange={e => setScheduleCourierChoice(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', border: '0.5px solid rgba(27,42,74,0.2)', borderRadius: 6, fontSize: 13, marginBottom: scheduleCourierChoice === 'Others' ? 10 : 14, fontFamily: 'var(--font)', background: '#fff' }}
                >
                  <option value="">Select courier partner</option>
                  {courierList.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                  <option value="Others">Others</option>
                </select>
                {scheduleCourierChoice === 'Others' && (
                  <div style={{ marginBottom: 14 }}>
                    <input
                      type="text" placeholder="Courier partner name" value={scheduleCourierOther}
                      onChange={e => setScheduleCourierOther(e.target.value)}
                      style={{ width: '100%', padding: '10px 12px', border: '0.5px solid rgba(27,42,74,0.2)', borderRadius: 6, fontSize: 13, marginBottom: 8, fontFamily: 'var(--font)' }}
                    />
                    <input
                      type="text" placeholder="Tracking URL — put {AWB} where the number goes, e.g. https://x.com/track?id={AWB}" value={scheduleCourierUrl}
                      onChange={e => setScheduleCourierUrl(e.target.value)}
                      style={{ width: '100%', padding: '10px 12px', border: '0.5px solid rgba(27,42,74,0.2)', borderRadius: 6, fontSize: 13, marginBottom: 4, fontFamily: 'var(--font)' }}
                    />
                    <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 8 }}>
                      Optional — leave blank to use a generic search-based tracking link for now instead.
                    </div>
                    <button
                      type="button"
                      onClick={() => handleAddCourier(scheduleCourierOther, scheduleCourierUrl, 'schedule')}
                      disabled={!scheduleCourierOther.trim() || courierAddedFlash === 'schedule'}
                      style={{ width: '100%', padding: 10, background: courierAddedFlash === 'schedule' ? '#1B7A3D' : (scheduleCourierOther.trim() ? 'var(--blue)' : '#ccc'), color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: scheduleCourierOther.trim() ? 'pointer' : 'not-allowed', fontFamily: 'var(--font)' }}
                    >
                      {courierAddedFlash === 'schedule' ? 'Partner added ✓' : '+ Add to courier list'}
                    </button>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setScheduleModal(null)} style={{ flex: 1, padding: '10px', background: '#eee', color: 'var(--navy)', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Cancel</button>
                  <button onClick={submitSchedule} disabled={busyKey === itemKeyFor(scheduleModal.orderId, scheduleModal.productId)} style={{ flex: 1, padding: '10px', background: '#1B7A3D', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
                    {busyKey === itemKeyFor(scheduleModal.orderId, scheduleModal.productId) ? 'Saving…' : 'Confirm Scheduled'}
                  </button>
                </div>
              </div>
            </div>
          )}
          {manualDeliveryModal && (
            <div
              onClick={() => setManualDeliveryModal(null)}
              style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}
            >
              <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, padding: '1.25rem', width: '100%', maxWidth: 340 }}>
                <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--navy)', marginBottom: 4 }}>Confirm real delivery — {manualDeliveryModal.productId}</p>
                <p style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 12 }}>Enter the ACTUAL delivery date/time from the courier's tracking page — this sets when the return window and cashback timer start, not today's date.</p>
                <input type="date" value={manualDeliveryDate} onChange={e => setManualDeliveryDate(e.target.value)} style={{ width: '100%', padding: '10px 12px', border: '0.5px solid rgba(27,42,74,0.2)', borderRadius: 6, fontSize: 13, marginBottom: 10 }} />
                <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                  <select value={manualDeliveryHour} onChange={e => setManualDeliveryHour(e.target.value)} style={{ flex: 1, padding: '10px 8px', border: '0.5px solid rgba(27,42,74,0.2)', borderRadius: 6, fontSize: 13, background: '#fff' }}>
                    <option value="">Hour</option>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                  <select value={manualDeliveryMinute} onChange={e => setManualDeliveryMinute(e.target.value)} style={{ flex: 1, padding: '10px 8px', border: '0.5px solid rgba(27,42,74,0.2)', borderRadius: 6, fontSize: 13, background: '#fff' }}>
                    <option value="">Min</option>
                    {['00', '15', '30', '45'].map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <select value={manualDeliveryAmPm} onChange={e => setManualDeliveryAmPm(e.target.value)} style={{ flex: 1, padding: '10px 8px', border: '0.5px solid rgba(27,42,74,0.2)', borderRadius: 6, fontSize: 13, background: '#fff' }}>
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </select>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setManualDeliveryModal(null)} style={{ flex: 1, padding: '10px', background: '#eee', color: 'var(--navy)', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Cancel</button>
                  <button
                    onClick={submitManualDelivery}
                    disabled={busyKey === itemKeyFor(manualDeliveryModal.orderId, manualDeliveryModal.productId) || !manualDeliveryDate || !manualDeliveryHour || !manualDeliveryMinute}
                    style={{ flex: 1, padding: '10px', background: 'var(--blue)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}
                  >
                    {busyKey === itemKeyFor(manualDeliveryModal.orderId, manualDeliveryModal.productId) ? 'Saving…' : 'Confirm Delivery'}
                  </button>
                </div>
              </div>
            </div>
          )}
          {msg && (
            <div style={{
              background: msg.startsWith('Error:') ? '#FBEAEA' : '#EEF4FF',
              color: msg.startsWith('Error:') ? '#B02A2A' : 'var(--navy)',
              border: msg.startsWith('Error:') ? '1px solid #E8A0A0' : 'none',
              fontSize: 12, padding: '10px 12px', borderRadius: 6, marginBottom: 14,
            }}>
              {msg}
              <button onClick={() => setMsg('')} style={{ float: 'right', background: 'none', border: 'none', color: msg.startsWith('Error:') ? '#B02A2A' : 'var(--blue)', cursor: 'pointer', fontSize: 11 }}>Dismiss</button>
            </div>
          )}
          {tab === 'dashboard' && (
            <>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
                <div className="dash-metric">
                  <div className="dash-metric-label">Today's orders</div>
                  <div className="dash-metric-value">{dashboardMetrics.todaysOrders}</div>
                </div>
                <div className="dash-metric">
                  <div className="dash-metric-label">Pending returns</div>
                  <div className="dash-metric-value">{dashboardMetrics.pendingReturns}</div>
                </div>
                <div className="dash-metric">
                  <div className="dash-metric-label">Revenue this week</div>
                  <div className="dash-metric-value">₹{dashboardMetrics.revenueWeek.toLocaleString()}</div>
                </div>
                <div className="dash-metric">
                  <div className="dash-metric-label">Revenue this month</div>
                  <div className="dash-metric-value">₹{dashboardMetrics.revenueMonth.toLocaleString()}</div>
                </div>
              </div>
              <div className="dash-section-label">Order lifecycle</div>
              <div className="dash-pill-row">
                {LIFECYCLE_BUCKETS.map(b => (
                  <button
                    key={b.key}
                    className={`dash-pill ${lifecycleFilter === b.key ? 'active' : ''}`}
                    onClick={() => { setLifecycleFilter(lifecycleFilter === b.key ? null : b.key); setReturnSubStage(null); setFilterOrderId(''); }}
                  >
                    {b.label} ({bucketCounts[b.key] || 0})
                  </button>
                ))}
              </div>
              {lifecycleFilter === 'return_progress' && (
                <div className="dash-pill-row dash-pill-row-sub">
                  {RETURN_SUB_STAGES.map(s => (
                    <button
                      key={s}
                      className={`dash-pill dash-pill-sm ${returnSubStage === s ? 'active' : ''}`}
                      onClick={() => { setReturnSubStage(returnSubStage === s ? null : s); setFilterOrderId(''); }}
                    >
                      {s} ({subStageCounts[s] || 0})
                    </button>
                  ))}
                </div>
              )}
              {lifecycleFilter && pillOrderIds.length > 0 && (
                <div className="dash-pill-row dash-pill-row-orders">
                  {pillOrderIds.map(oid => (
                    <button
                      key={oid}
                      className={`dash-pill dash-pill-sm ${filterOrderId === oid ? 'active' : ''}`}
                      onClick={() => setFilterOrderId(filterOrderId === oid ? '' : oid)}
                    >
                      ...{oid.slice(-4)}
                    </button>
                  ))}
                </div>
              )}
              <div className="dash-section-label" style={{ marginTop: 18 }}>Look up by</div>
              <div className="dash-pill-row" style={{ position: 'relative' }}>
                {[['name', 'ti-user', 'Customer name'], ['phone', 'ti-phone', 'Phone'], ['orderid', 'ti-receipt', 'Order ID'], ['prodid', 'ti-package', 'Product ID']].map(([mode, icon, label]) => (
                  <button
                    key={mode}
                    className={`dash-pill ${lookupMode === mode ? 'active' : ''}`}
                    onClick={() => { setLookupMode(lookupMode === mode ? null : mode); setLookupQuery(''); }}
                  >
                    <i className={`ti ${icon}`} style={{ fontSize: 13, marginRight: 4, verticalAlign: -2 }}></i>{label}
                  </button>
                ))}
              </div>
              {lookupMode && (
                <div className="dash-lookup-box">
                  <input
                    type="text" autoFocus placeholder="Type to search…" value={lookupQuery}
                    onChange={e => setLookupQuery(e.target.value)}
                    style={{ width: '100%', padding: '8px 10px', border: '0.5px solid rgba(27,42,74,0.2)', borderRadius: 6, fontSize: 12, marginBottom: 8, fontFamily: 'var(--font)' }}
                  />
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 140, overflowY: 'auto' }}>
                    {lookupFiltered.length === 0 ? (
                      <span style={{ fontSize: 11, color: 'var(--muted)' }}>No matches.</span>
                    ) : lookupFiltered.map(v => (
                      <button key={v} className="dash-pill dash-pill-sm" onClick={() => applyLookupSelection(v)}>{v}</button>
                    ))}
                  </div>
                </div>
              )}
              {(filterProdId || filterOrderId || filterCustomer) && (
                <div className="dash-active-filters">
                  {filterProdId && <span className="dash-filter-chip">Product: {filterProdId} <button onClick={() => setFilterProdId('')}>×</button></span>}
                  {filterOrderId && <span className="dash-filter-chip">Order: ...{filterOrderId.slice(-4)} <button onClick={() => setFilterOrderId('')}>×</button></span>}
                  {filterCustomer && <span className="dash-filter-chip">Customer: {filterCustomer} <button onClick={() => setFilterCustomer('')}>×</button></span>}
                  <button className="dash-clear-all" onClick={clearAllFilters}>Clear all</button>
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, marginTop: 16, marginBottom: 4 }}>
                <input
                  type="text" placeholder="Search audit trail — order, product, action" value={auditSearch}
                  onChange={e => setAuditSearch(e.target.value)}
                  style={{ flex: 1, padding: '9px 12px', border: '0.5px solid rgba(27,42,74,0.2)', borderRadius: 6, fontSize: 12, fontFamily: 'var(--font)' }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 11, color: 'var(--muted)' }}>
                  {showFullHistory || hasActiveFilter ? `Showing ${filteredAuditEntries.length} entries` : `Showing last 15 days (${filteredAuditEntries.length} entries)`}
                </span>
                {!hasActiveFilter && (
                  <button onClick={() => setShowFullHistory(v => !v)} style={{ fontSize: 11, color: 'var(--blue)', background: 'none', border: 'none', cursor: 'pointer' }}>
                    {showFullHistory ? 'Show last 15 days only' : 'Show all history'}
                  </button>
                )}
              </div>
              <div className="dash-audit-table">
                <div className="dash-audit-header" style={{ gridTemplateColumns: dashGridTemplate }}>
                  {[['ts', 'date', 'Timestamp'], ['order', 'orderId', 'Order'], ['prod', 'prodId', 'Prod ID'], ['name', 'customerName', 'Customer'], ['phone', 'customerPhone', 'Phone'], ['action', 'action', 'Action'], ['by', null, 'By']].map(([colKey, sortField, label], i) => (
                    <span key={colKey} className={`dash-col-${colKey}`} style={{ position: 'relative', cursor: sortField ? 'pointer' : 'default' }} onClick={() => sortField && toggleSort(sortField)}>
                      {label}{sortKey === sortField && sortField && (sortDir === 'asc' ? ' ↑' : ' ↓')}
                      <span className="dash-resize-handle" onMouseDown={e => { e.stopPropagation(); startResize(colKey, e); }} onClick={e => e.stopPropagation()}></span>
                    </span>
                  ))}
                  <span></span>
                </div>
                {filteredAuditEntries.length === 0 ? (
                  <div style={{ padding: 20, textAlign: 'center', fontSize: 12, color: 'var(--muted)' }}>No matching entries.</div>
                ) : filteredAuditEntries.slice(0, 200).map((row, idx) => (
                  <div key={idx} className="dash-audit-row" style={{ gridTemplateColumns: dashGridTemplate }}>
                    <span className="dash-col-ts">{formatDateTimeWithSeconds(row.date)}</span>
                    <span className={`dash-col-order ${row.isNewOrder ? 'txt-green' : ''}`}>{row.orderId}</span>
                    <span className="dash-col-prod">{row.prodId}</span>
                    <span className="dash-col-name">{row.customerName || '—'}</span>
                    <span className="dash-col-phone">{row.customerPhone || '—'}</span>
                    <span className={`dash-col-action ${row.isNewOrder ? 'txt-green' : row.isReturn ? 'txt-red' : row.isWhatsApp ? 'txt-whatsapp' : ''}`}>
                      {row.isWhatsApp && <i className="ti ti-brand-whatsapp" style={{ fontSize: 12, marginRight: 4, verticalAlign: -1 }}></i>}
                      {row.action}
                    </span>
                    <span className="dash-col-by">{row.by}</span>
                    <button aria-label="Copy" onClick={() => copyAuditRow(row, idx)} className="dash-audit-copy">
                      {copiedRowIdx === idx ? <i className="ti ti-check" style={{ color: '#1B7A3D' }}></i> : <i className="ti ti-copy"></i>}
                    </button>
                  </div>
                ))}
                {filteredAuditEntries.length > 200 && (
                  <div style={{ padding: 10, textAlign: 'center', fontSize: 11, color: 'var(--muted)' }}>
                    Showing first 200 of {filteredAuditEntries.length} — narrow your search to see more specific results.
                  </div>
                )}
              </div>
            </>
          )}
          {tab === 'orders' && (
            <>
              <div className="dash-section-label">Order lifecycle</div>
              <div className="dash-pill-row">
                {LIFECYCLE_BUCKETS.map(b => (
                  <button
                    key={b.key}
                    className={`dash-pill ${ordersLifecycleFilter === b.key ? 'active' : ''}`}
                    onClick={() => { setOrdersLifecycleFilter(ordersLifecycleFilter === b.key ? null : b.key); setOrdersReturnSubStage(null); setOrdersFilterOrderId(''); }}
                  >
                    {b.label} ({bucketCounts[b.key] || 0})
                  </button>
                ))}
                <button
                  className={`dash-pill ${showUnackList ? 'active' : ''}`}
                  style={unacknowledgedParcels.length > 0 && !showUnackList ? { borderColor: '#D64545', color: '#D64545' } : undefined}
                  onClick={() => setShowUnackList(v => !v)}
                >
                  Unacknowledged &gt;10 days ({unacknowledgedParcels.length})
                </button>
              </div>
              {showUnackList && (
                <div className="unack-banner">
                  {unacknowledgedParcels.length === 0 ? (
                    <div className="unack-banner-header" style={{ color: 'var(--muted)' }}>
                      <i className="ti ti-check" style={{ fontSize: 16, marginRight: 6 }} aria-hidden="true"></i>
                      No unacknowledged parcels right now.
                    </div>
                  ) : (
                    <>
                      <div className="unack-banner-header">
                        <i className="ti ti-alert-triangle" style={{ fontSize: 16, marginRight: 6 }} aria-hidden="true"></i>
                        {unacknowledgedParcels.length} item{unacknowledgedParcels.length !== 1 ? 's' : ''} dispatched over 10 days ago, never confirmed received — check the courier's real delivery date
                      </div>
                      <div className="unack-banner-list">
                        {unacknowledgedParcels.map(row => (
                          <div key={row.orderId + '|' + row.productId} className="unack-row">
                            <span className="unack-row-text">{row.productId} — {row.orderId} ({row.customerName || 'Guest'})</span>
                            <button
                              onClick={() => openManualDeliveryModal(row.orderId, row.productId, row.lineKey)}
                              className="unack-row-btn"
                            >
                              Confirm real delivery
                            </button>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
              {ordersLifecycleFilter === 'return_progress' && (
                <div className="dash-pill-row dash-pill-row-sub">
                  {RETURN_SUB_STAGES.map(s => (
                    <button
                      key={s}
                      className={`dash-pill dash-pill-sm ${ordersReturnSubStage === s ? 'active' : ''}`}
                      onClick={() => { setOrdersReturnSubStage(ordersReturnSubStage === s ? null : s); setOrdersFilterOrderId(''); }}
                    >
                      {s} ({subStageCounts[s] || 0})
                    </button>
                  ))}
                </div>
              )}
              {ordersLifecycleFilter && ordersPillOrderIds.length > 0 && (
                <div className="dash-pill-row dash-pill-row-orders">
                  {ordersPillOrderIds.map(oid => (
                    <button
                      key={oid}
                      className={`dash-pill dash-pill-sm ${ordersFilterOrderId === oid ? 'active' : ''}`}
                      onClick={() => setOrdersFilterOrderId(ordersFilterOrderId === oid ? '' : oid)}
                    >
                      ...{oid.slice(-4)}
                    </button>
                  ))}
                </div>
              )}
              {ordersLifecycleFilter && (
                <div className="dash-active-filters" style={{ marginBottom: 12 }}>
                  <span className="dash-filter-chip">
                    {LIFECYCLE_BUCKETS.find(b => b.key === ordersLifecycleFilter)?.label}{ordersReturnSubStage ? ` · ${ordersReturnSubStage}` : ''}{ordersFilterOrderId ? ` · ...${ordersFilterOrderId.slice(-4)}` : ''}
                    <button onClick={() => { setOrdersLifecycleFilter(null); setOrdersReturnSubStage(null); setOrdersFilterOrderId(''); }}>×</button>
                  </span>
                  <button className="dash-clear-all" onClick={() => { setOrdersLifecycleFilter(null); setOrdersReturnSubStage(null); setOrdersFilterOrderId(''); }}>Clear all</button>
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                <button onClick={exportCSV} className="toolbar-control" style={{ background: '#fff', border: '0.5px solid rgba(27,42,74,0.25)', color: 'var(--navy)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  <i className="ti ti-download"></i>Export ({filteredOrders.length})
                </button>
                <button onClick={() => setShowDateFilter(v => !v)} className="toolbar-control" style={{ background: showDateFilter ? '#EEF4FF' : '#fff', border: '0.5px solid rgba(27,42,74,0.25)', color: 'var(--navy)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  <i className="ti ti-calendar"></i>Date range
                </button>
                <input
                  type="text" placeholder="Order # / Customer ID / phone / name" value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="toolbar-control"
                  style={{ border: '0.5px solid rgba(27,42,74,0.2)', fontFamily: 'var(--font)' }}
                />
                <input
                  type="text" placeholder="Product ID" value={productSearch}
                  onChange={e => setProductSearch(e.target.value)}
                  className="toolbar-control"
                  style={{ border: '0.5px solid rgba(27,42,74,0.2)', fontFamily: 'var(--font)' }}
                />
              </div>
              {showDateFilter && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
                  <label style={{ fontSize: 11, color: 'var(--muted)' }}>From</label>
                  <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ padding: '6px 8px', border: '0.5px solid rgba(27,42,74,0.2)', borderRadius: 6, fontSize: 12, fontFamily: 'var(--font)' }} />
                  <label style={{ fontSize: 11, color: 'var(--muted)' }}>To</label>
                  <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ padding: '6px 8px', border: '0.5px solid rgba(27,42,74,0.2)', borderRadius: 6, fontSize: 12, fontFamily: 'var(--font)' }} />
                  {(dateFrom || dateTo) && (
                    <button onClick={() => { setDateFrom(''); setDateTo(''); }} style={{ fontSize: 11, color: 'var(--blue)', background: 'none', border: 'none', cursor: 'pointer' }}>Clear</button>
                  )}
                </div>
              )}
              {ordersLoading ? (
                <div style={{ fontSize: 13, color: 'var(--muted)' }}>Loading orders…</div>
              ) : filteredOrders.length === 0 ? (
                <div style={{ fontSize: 13, color: 'var(--muted)' }}>No orders found.</div>
              ) : (
                filteredOrders.map(o => {
                  const items = o.items || [];
                  const allDispatched = items.length > 0 && items.every(it => it.dispatchedDate);
                  const anyReturnPending = items.some(it => ['Requested', 'Approved', 'Scheduled', 'Picked'].includes(it.returnStage));
                  let orderLevelAction = null;
                  if (allDispatched && !anyReturnPending && o.status === 'Paid' && o.orderStatus !== 'Out of Stock') {
                    orderLevelAction = { label: 'Send Delivery Reminder', color: 'var(--gold)', onClick: () => sendDeliveryReminder(o.orderId) };
                  } else if (o.status === 'Paid' && o.orderStatus !== 'Out of Stock' && !allDispatched) {
                    orderLevelAction = { label: 'Mark Out-of-Stock', color: '#8A5A00', onClick: () => markOutOfStock(o.orderId) };
                  }
                  return (
                    <div key={o.orderId} className="card" style={{ marginBottom: 10, position: 'relative' }}>
                      <div
                        onClick={() => setExpandedOrderId(expandedOrderId === o.orderId ? null : o.orderId)}
                        style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
                      >
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--navy)' }}>{o.orderId}</div>
                          <div style={{ fontSize: 11, color: 'var(--muted)' }}>{o.customerId || 'Guest'} · ₹{o.orderTotal} · {o.status}{o.orderStatus === 'Out of Stock' ? ' · Out of Stock' : ''} · {items.length} item{items.length !== 1 ? 's' : ''}</div>
                          <div style={{ fontSize: 10, color: '#999', marginTop: 2 }}>{formatDateTime(o.timestamp)}</div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }} onClick={e => e.stopPropagation()}>
                          <button
                            onClick={() => setOpenMenuOrderId(openMenuOrderId === o.orderId ? null : o.orderId)}
                            aria-label="More actions"
                            style={{ border: 'none', background: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 16, padding: 4 }}
                          >
                            <i className="ti ti-dots"></i>
                          </button>
                        </div>
                      </div>
                      {expandedOrderId !== o.orderId && (
                        <div className="admin-collapsed-pills" onClick={e => e.stopPropagation()}>
                          {items.map((it, idx) => {
                            const display = getItemStageDisplay(it);
                            const nextAction = getAdminNextAction(it);
                            const itemKey = itemKeyFor(o.orderId, it.productId);
                            const itemBusy = busyKey === itemKey;
                            const hasAttachment = !!(it.attachmentImageUrl && it.attachmentSize && it.attachmentImageUrl !== 'Deleted after cashback credited');
                            const isLastItem = idx === items.length - 1;
                            const showPill = !(display.key === 'preparing' && o.orderStatus !== 'Out of Stock');
                            return (
                              <div key={it.productId} className="admin-collapsed-pill-group">
                                {showPill && (
                                  <span className="admin-collapsed-status-pill" style={{ background: display.color + '1A', color: display.color }}>
                                    {it.productId}: {o.orderStatus === 'Out of Stock' ? 'Out of Stock' : adminItemStatusLabel(display)}
                                  </span>
                                )}
                                {!it.dispatchedDate && o.orderStatus !== 'Out of Stock' && (
                                  <button
                                    onClick={() => openDispatchModal(o.orderId, it.productId, it.lineKey)}
                                    disabled={itemBusy || !hasAttachment}
                                    className="admin-collapsed-action-btn"
                                    style={{ background: hasAttachment ? 'var(--blue)' : '#ccc' }}
                                    title={hasAttachment ? '' : "Waiting on the customer's color/size selection to sync"}
                                  >
                                    Mark Dispatched
                                  </button>
                                )}
                                {nextAction && nextAction.key === 'mark_scheduled' && (
                                  <button onClick={() => openScheduleModal(o.orderId, it.productId, it.lineKey)} disabled={itemBusy} className="admin-collapsed-action-btn" style={{ background: nextAction.color }}>
                                    {nextAction.label}
                                  </button>
                                )}
                                {nextAction && nextAction.key === 'mark_picked' && (
                                  <button onClick={() => markReturnPicked(o.orderId, it.productId, it.lineKey)} disabled={itemBusy} className="admin-collapsed-action-btn" style={{ background: nextAction.color }}>
                                    {itemBusy ? 'Working…' : nextAction.label}
                                  </button>
                                )}
                                {nextAction && nextAction.key === 'mark_received' && (
                                  <button onClick={() => markReturnReceived(o.orderId, it.productId, it.lineKey)} disabled={itemBusy} className="admin-collapsed-action-btn" style={{ background: nextAction.color }}>
                                    {itemBusy ? 'Working…' : nextAction.label}
                                  </button>
                                )}
                                {nextAction && nextAction.key === 'approve_or_reject' && (
                                  <span className="admin-collapsed-status-pill" style={{ background: '#FEF8E8', color: '#854F0B' }}>
                                    Needs review — tap to open
                                  </span>
                                )}
                                {isLastItem && orderLevelAction && (
                                  <button
                                    onClick={orderLevelAction.onClick}
                                    disabled={busyKey === o.orderId}
                                    className="admin-collapsed-action-btn"
                                    style={{ background: orderLevelAction.color }}
                                  >
                                    {busyKey === o.orderId ? 'Working…' : orderLevelAction.label}
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {openMenuOrderId === o.orderId && (
                        <div style={{ position: 'absolute', right: 12, top: 36, background: '#fff', border: '0.5px solid rgba(27,42,74,0.15)', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 10, minWidth: 180 }}>
                          <button
                            onClick={() => { setExpandedOrderId(o.orderId); setOpenMenuOrderId(null); }}
                            style={{ width: '100%', textAlign: 'left', padding: '10px 12px', background: 'none', border: 'none', fontSize: 12, color: 'var(--navy)', cursor: 'pointer', display: 'block' }}
                          >
                            View full details
                          </button>
                          <button
                            onClick={() => { setExpandedOrderId(o.orderId); setOpenMenuOrderId(null); }}
                            style={{ width: '100%', textAlign: 'left', padding: '10px 12px', background: 'none', border: 'none', fontSize: 12, color: 'var(--navy)', cursor: 'pointer', display: 'block', borderTop: '0.5px solid var(--border)' }}
                          >
                            View activity log
                          </button>
                        </div>
                      )}
                      {expandedOrderId === o.orderId && (
                      <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {orderLevelAction && (
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <button
                              onClick={orderLevelAction.onClick}
                              disabled={busyKey === o.orderId}
                              style={{ padding: '8px 12px', background: orderLevelAction.color, color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}
                            >
                              {busyKey === o.orderId ? 'Working…' : orderLevelAction.label}
                            </button>
                          </div>
                        )}
                        {items.map(it => {
                          const track = trackingSearchUrl(it, courierList);
                          const hasAttachment = !!(it.attachmentImageUrl && it.attachmentSize && it.attachmentImageUrl !== 'Deleted after cashback credited');
                          const thumbUrl = (it.attachmentImageUrl && it.attachmentImageUrl !== 'Deleted after cashback credited') ? it.attachmentImageUrl : '';
                          const itemKey = itemKeyFor(o.orderId, it.productId);
                          const nextAction = getAdminNextAction(it);
                          const itemBusy = busyKey === itemKey;
                          return (
                            <div key={it.productId} style={{ background: '#F4F2EE', borderRadius: 6, padding: '8px 10px', display: 'flex', gap: 8 }}>
                              <div style={{ width: 40, height: 40, borderRadius: 5, background: '#fff', border: '0.5px solid rgba(27,42,74,0.15)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                {thumbUrl ? (
                                  <img src={thumbUrl} alt={it.itemDescriptor} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                  <i className="ti ti-photo" style={{ fontSize: 16, color: '#ccc' }}></i>
                                )}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
                                  <div style={{ fontSize: 12, color: 'var(--navy)', fontWeight: 500 }}>{it.itemDescriptor}</div>
                                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                    {o.orderStatus === 'Out of Stock' ? (
                                      <span style={{ padding: '5px 9px', background: '#F4E0C0', color: '#8A5A00', borderRadius: 5, fontSize: 10 }}>Out of Stock — won't ship</span>
                                    ) : !it.dispatchedDate ? (
                                      <button
                                        onClick={() => openDispatchModal(o.orderId, it.productId, it.lineKey)}
                                        disabled={itemBusy}
                                        style={{ padding: '5px 9px', background: hasAttachment ? 'var(--blue)' : '#ccc', color: '#fff', border: 'none', borderRadius: 5, fontSize: 10, cursor: hasAttachment ? 'pointer' : 'not-allowed' }}
                                        title={hasAttachment ? '' : 'Waiting on the customer\'s color/size selection to sync — check back shortly'}
                                      >
                                        Mark Dispatched
                                      </button>
                                    ) : (
                                      <span style={{ padding: '5px 9px', background: '#DDEFD3', color: '#173404', borderRadius: 5, fontSize: 10 }}>Dispatched ✓</span>
                                    )}
                                    {nextAction && nextAction.key === 'approve_or_reject' && (
                                      <>
                                        <button
                                          onClick={() => approveReturn(o.orderId, it.productId, it.lineKey)}
                                          disabled={itemBusy}
                                          style={{ padding: '5px 9px', background: '#1B7A3D', color: '#fff', border: 'none', borderRadius: 5, fontSize: 10, cursor: 'pointer' }}
                                        >
                                          {itemBusy ? 'Working…' : nextAction.approveLabel}
                                        </button>
                                        <button
                                          onClick={() => openRejectModal(o.orderId, it.productId, it.lineKey)}
                                          disabled={itemBusy}
                                          style={{ padding: '5px 9px', background: '#D64545', color: '#fff', border: 'none', borderRadius: 5, fontSize: 10, cursor: 'pointer' }}
                                        >
                                          {nextAction.rejectLabel}
                                        </button>
                                      </>
                                    )}
                                    {nextAction && nextAction.key === 'mark_scheduled' && (
                                      <button
                                        onClick={() => openScheduleModal(o.orderId, it.productId, it.lineKey)}
                                        disabled={itemBusy}
                                        style={{ padding: '5px 9px', background: nextAction.color, color: '#fff', border: 'none', borderRadius: 5, fontSize: 10, cursor: 'pointer' }}
                                      >
                                        {nextAction.label}
                                      </button>
                                    )}
                                    {nextAction && nextAction.key === 'mark_picked' && (
                                      <button
                                        onClick={() => markReturnPicked(o.orderId, it.productId, it.lineKey)}
                                        disabled={itemBusy}
                                        style={{ padding: '5px 9px', background: nextAction.color, color: '#fff', border: 'none', borderRadius: 5, fontSize: 10, cursor: 'pointer' }}
                                      >
                                        {itemBusy ? 'Working…' : nextAction.label}
                                      </button>
                                    )}
                                    {nextAction && nextAction.key === 'mark_received' && (
                                      <button
                                        onClick={() => markReturnReceived(o.orderId, it.productId, it.lineKey)}
                                        disabled={itemBusy}
                                        style={{ padding: '5px 9px', background: nextAction.color, color: '#fff', border: 'none', borderRadius: 5, fontSize: 10, cursor: 'pointer' }}
                                      >
                                        {itemBusy ? 'Working…' : nextAction.label}
                                      </button>
                                    )}
                                  </div>
                                </div>
                                {it.dispatchedDate && (
                                  <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>
                                    {it.awb} ({it.courierPartner || 'courier'}){track && (
                                      <a href={track} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 6, color: 'var(--blue)' }}>Track →</a>
                                    )}
                                  </div>
                                )}
                                {it.attachmentSize && (
                                  <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>
                                    Size/color: {it.attachmentSize}
                                    {it.attachmentImageUrl === 'Deleted after cashback credited' && ' · photo deleted (cashback already credited)'}
                                  </div>
                                )}
                                {it.returnStage && (
                                  <div style={{ fontSize: 10, color: '#8A5A00', marginTop: 2 }}>
                                    Return: {it.returnStage}{it.returnReason ? ` — ${it.returnReason}` : ''}
                                  </div>
                                )}
                                {it.cashbackStatus === 'Credited' && (
                                  <div style={{ fontSize: 10, color: '#1B7A3D', marginTop: 2 }}>Cashback credited ✓</div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      )}
                      {expandedOrderId === o.orderId && (
                        <div style={{ marginTop: 12, borderTop: '0.5px solid var(--border)', paddingTop: 12, fontSize: 12, color: '#555', lineHeight: 1.7 }}>
                          <div><strong>Customer:</strong> {o.customerName} · {o.customerPhone}</div>
                          <div><strong>Address:</strong> {o.address}</div>
                          <div><strong>Shipping:</strong> {o.shippingType}</div>
                          <div><strong>Payment ID:</strong> {o.paymentId || '—'}</div>
                          <div><strong>Wallet used:</strong> ₹{o.walletUsed}</div>
                          <div><strong>Order status:</strong> {o.orderStatus || '—'}</div>
                          {o.refundStatus && <div><strong>Refund status:</strong> {o.refundStatus}</div>}
                          {o.deliveryIssueNote && <div><strong>Delivery issue note:</strong> {o.deliveryIssueNote}</div>}
                          <div style={{ marginTop: 10 }}>
                            <div style={{ fontWeight: 600, color: 'var(--navy)', marginBottom: 6 }}>Activity</div>
                            {(() => {
                              const lines = [];
                              let lineSeq = 0;
                              items.forEach(it => {
                                const a = it.activity;
                                if (!a) return;
                                const cells = [
                                  a.dispatched, a.deliveryReminderSent, a.parcelReceived,
                                  a.returnRequested, a.returnApprovedRejected, a.returnScheduled,
                                  a.returnPicked, a.returnReceived, a.refundWalletChoice, a.walletCreditedRefund, a.outOfStock,
                                ];
                                cells.forEach(cell => {
                                  if (!cell) return;
                                  const sepIdx = cell.indexOf(' — ');
                                  const tsText = sepIdx > -1 ? cell.slice(0, sepIdx) : '';
                                  const detail = sepIdx > -1 ? cell.slice(sepIdx + 3) : cell;
                                  lines.push({ tsText, detail, productId: it.productId, sortTs: Date.parse(tsText) || 0, seq: lineSeq++ });
                                });
                              });
                              lines.sort((x, y) => (y.sortTs - x.sortTs) || (y.seq - x.seq));
                              return lines.length === 0 ? (
                                <div style={{ color: 'var(--muted)', fontStyle: 'italic' }}>No actions taken on this order yet.</div>
                              ) : (
                                <div style={{ background: '#F4F2EE', borderRadius: 6, padding: 8, maxHeight: 180, overflowY: 'auto' }}>
                                  {lines.map((line, idx) => (
                                    <div key={idx} style={{ padding: '4px 0', borderBottom: idx < lines.length - 1 ? '0.5px solid var(--border)' : 'none', fontSize: 12 }}>
                                      {line.tsText} — [{line.productId}] {line.detail}
                                    </div>
                                  ))}
                                </div>
                              );
                            })()}
                          </div>
                          <div style={{ marginTop: 10 }}>
                            <input
                              type="text" placeholder="Delivery issue note" value={noteDrafts[o.orderId] || ''}
                              onChange={e => setNoteDrafts(prev => ({ ...prev, [o.orderId]: e.target.value }))}
                              style={{ width: '100%', padding: '8px 10px', border: '0.5px solid rgba(27,42,74,0.2)', borderRadius: 6, fontSize: 12, marginBottom: 6, fontFamily: 'var(--font)' }}
                            />
                            <button
                              onClick={() => flagDeliveryIssue(o.orderId)}
                              disabled={busyKey === o.orderId}
                              style={{ padding: '8px 12px', background: '#D64545', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}
                            >
                              Submit Flag
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </>
          )}
          {tab === 'products' && (
            <div className="card">
              <div className="section-title" style={{ marginBottom: 10 }}>Product quick-edit</div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                <input
                  type="text" placeholder="Search by Prod ID or title" value={productSearchQuery}
                  onChange={e => setProductSearchQuery(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') searchProducts(); }}
                  className="toolbar-control"
                  style={{ border: '0.5px solid rgba(27,42,74,0.2)', fontFamily: 'var(--font)' }}
                />
                <button onClick={searchProducts} disabled={productSearchBusy} className="toolbar-control" style={{ background: 'var(--blue)', color: '#fff', border: 'none', cursor: 'pointer' }}>
                  {productSearchBusy ? '...' : 'Search'}
                </button>
              </div>
              {productResults.length === 0 ? (
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>No results yet — search above.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {productResults.map(p => (
                    <div key={p.prodId} style={{ background: '#F4F2EE', borderRadius: 6, padding: '8px 10px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--navy)' }}>{p.prodId}</div>
                          <div style={{ fontSize: 11, color: 'var(--muted)' }}>{p.title} · ₹{p.price}</div>
                        </div>
                        <span style={{
                          fontSize: 10, fontWeight: 600, padding: '3px 7px', borderRadius: 5,
                          background: p.status === 'ACTIVE' ? '#DDEFD3' : p.status === 'SOLD' ? '#F4F2EE' : '#FEF8E8',
                          color: p.status === 'ACTIVE' ? '#173404' : p.status === 'SOLD' ? 'var(--muted)' : '#854F0B',
                        }}>{p.status}</span>
                      </div>
                      <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                        {['ACTIVE', 'HIDDEN', 'SOLD'].filter(s => s !== p.status).map(s => (
                          <button
                            key={s}
                            onClick={() => updateProductStatus(p.prodId, s)}
                            disabled={productUpdateBusyId === p.prodId}
                            style={{ padding: '5px 9px', background: '#fff', border: '0.5px solid rgba(27,42,74,0.2)', borderRadius: 5, fontSize: 10, cursor: 'pointer', color: 'var(--navy)' }}
                          >
                            {productUpdateBusyId === p.prodId ? 'Saving…' : `Mark ${s}`}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {tab === 'wallet' && (
            <div className="card">
              <div className="section-title" style={{ marginBottom: 10 }}>Look up customer wallet</div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
                <input
                  type="text" placeholder="Customer ID (e.g. NB12345)" value={walletCustomerId}
                  onChange={e => setWalletCustomerId(e.target.value)}
                  className="toolbar-control"
                  style={{ border: '0.5px solid rgba(27,42,74,0.2)', fontFamily: 'var(--font)' }}
                />
                <button onClick={lookupWallet} disabled={walletLoading} className="toolbar-control" style={{ background: 'var(--blue)', color: '#fff', border: 'none', cursor: 'pointer' }}>
                  {walletLoading ? '...' : 'Look up'}
                </button>
              </div>
              {walletData && (
                <div style={{ background: '#EEF4FF', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 12, color: 'var(--navy)', lineHeight: 1.8 }}>
                  <div><strong>Tier:</strong> {walletData.tier} ({(walletData.earnRate * 100).toFixed(1)}% earn rate)</div>
                  <div><strong>Lifetime spend:</strong> ₹{walletData.lifetimeSpend}</div>
                  <div><strong>Wallet balance:</strong> ₹{walletData.walletBalance}</div>
                  <div><strong>Redemption eligible:</strong> {walletData.isRedeemable ? 'Yes (up to 50% of order value)' : 'No — balance must exceed ₹25'}</div>
                </div>
              )}
              {walletData && (
                <>
                  <div className="section-title" style={{ marginBottom: 10 }}>Manual adjustment</div>
                  <input
                    type="number" placeholder="Amount (use negative to deduct, e.g. -50)" value={adjustAmount}
                    onChange={e => setAdjustAmount(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', border: '0.5px solid rgba(27,42,74,0.2)', borderRadius: 6, fontSize: 13, marginBottom: 10, fontFamily: 'var(--font)' }}
                  />
                  <input
                    type="text" placeholder="Reason (required, e.g. Goodwill credit for delayed order)" value={adjustReason}
                    onChange={e => setAdjustReason(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', border: '0.5px solid rgba(27,42,74,0.2)', borderRadius: 6, fontSize: 13, marginBottom: 10, fontFamily: 'var(--font)' }}
                  />
                  <button onClick={submitAdjustment} disabled={adjustBusy} className="btn-blue" style={{ width: '100%' }}>
                    {adjustBusy ? 'Saving…' : 'Apply Adjustment'}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      <style>{`
        .unack-banner { background: #FDECEC; border: 0.5px solid rgba(214,69,69,0.3); border-radius: 8px; padding: 10px 12px; margin-bottom: 16px; }
        .unack-banner-header { font-size: 12px; font-weight: 600; color: #B02A2A; display: flex; align-items: center; margin-bottom: 8px; }
        .unack-banner-list { display: flex; flex-direction: column; gap: 6px; }
        .unack-row { display: flex; justify-content: space-between; align-items: center; gap: 8px; background: #fff; border-radius: 6px; padding: 6px 8px; }
        .unack-row-text { font-size: 11px; color: var(--navy); }
        .unack-row-btn { background: var(--blue); color: #fff; border: none; border-radius: 5px; padding: 5px 9px; font-size: 10px; font-weight: 600; cursor: pointer; white-space: nowrap; }
        .admin-collapsed-pills { display: flex; flex-direction: column; gap: 6px; margin-top: 10px; }
        .admin-collapsed-pill-group { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
        .admin-collapsed-status-pill { font-size: 10px; font-weight: 600; padding: 4px 8px; border-radius: 12px; white-space: nowrap; }
        .admin-collapsed-action-btn { border: none; color: #fff; font-size: 10px; font-weight: 600; padding: 5px 9px; border-radius: 5px; cursor: pointer; white-space: nowrap; align-self: flex-start; }
        .admin-shell { display: flex; min-height: calc(100vh - 56px); }
        .admin-sidebar { flex-shrink: 0; background: #F4F2EE; border-right: 0.5px solid var(--border); transition: width 0.15s; }
        .sidebar-header { display: flex; align-items: center; justify-content: space-between; padding: 10px 8px; border-bottom: 0.5px solid var(--border); }
        .sidebar-title { font-size: 12px; font-weight: 600; color: var(--navy); }
        .sidebar-toggle { border: none; background: none; cursor: pointer; color: var(--muted); font-size: 16px; padding: 4px; }
        .sidebar-buttons { padding: 8px; display: flex; flex-direction: column; gap: 4px; }
        .sidebar-btn { width: 100%; display: flex; align-items: center; gap: 10px; padding: 10px 8px; border-radius: 6px; border: none; cursor: pointer; font-family: var(--font); font-size: 13px; }
        .admin-content { flex: 1; padding: 1rem; min-width: 0; padding-bottom: 60px; }
        .toolbar-control { width: 200px; height: 36px; padding: 0 12px; border-radius: 6px; font-size: 12px; box-sizing: border-box; }
        .dash-metric { flex: 1; min-width: 130px; background: #fff; border: 0.5px solid rgba(27,42,74,0.12); border-top: 3px solid var(--blue); border-radius: 10px; padding: 14px; box-shadow: 0 1px 3px rgba(0,0,0,0.04); }
        .dash-metric-label { font-size: 11px; color: var(--muted); margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.04em; }
        .dash-metric-value { font-size: 24px; font-weight: 600; color: var(--navy); }
        .dash-section-label { font-size: 12px; font-weight: 600; color: var(--navy); margin-bottom: 8px; }
        .dash-pill-row { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 10px; }
        .dash-pill-row-sub, .dash-pill-row-orders { padding-left: 8px; }
        .dash-pill { padding: 7px 12px; border-radius: 20px; border: 0.5px solid rgba(27,42,74,0.2); background: #fff; color: var(--navy); font-size: 12px; cursor: pointer; font-family: var(--font); white-space: nowrap; }
        .dash-pill.active { background: var(--navy); border-color: var(--navy); color: #fff; }
        .dash-pill-sm { padding: 5px 10px; font-size: 11px; }
        .dash-lookup-box { background: #F4F2EE; border-radius: 8px; padding: 10px; margin-bottom: 10px; }
        .dash-active-filters { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; margin-bottom: 10px; }
        .dash-filter-chip { display: inline-flex; align-items: center; gap: 6px; background: var(--navy); color: #fff; font-size: 11px; font-weight: 500; padding: 5px 10px; border-radius: 14px; }
        .dash-filter-chip button { background: none; border: none; color: #fff; cursor: pointer; font-size: 13px; line-height: 1; padding: 0; opacity: 0.8; }
        .dash-clear-all { font-size: 11px; color: var(--red); background: none; border: none; cursor: pointer; text-decoration: underline; }
        .dash-audit-table { border: 0.5px solid rgba(27,42,74,0.12); border-radius: 8px; overflow-x: auto; }
        .dash-audit-header { display: grid; gap: 8px; padding: 8px 12px; background: #F4F2EE; font-size: 10px; font-weight: 600; color: var(--muted); text-transform: uppercase; white-space: nowrap; }
        .dash-audit-row { display: grid; gap: 8px; padding: 9px 12px; border-top: 0.5px solid rgba(27,42,74,0.08); font-size: 11px; align-items: center; background: #fff; white-space: nowrap; overflow: hidden; }
        .dash-audit-row span { overflow: hidden; text-overflow: ellipsis; }
        .txt-green { color: #1B7A3D !important; font-weight: 500; }
        .txt-red { color: #D64545 !important; font-weight: 500; }
        .txt-whatsapp { color: #0F6E56 !important; font-weight: 600; }
        .dash-resize-handle { position: absolute; right: -4px; top: 0; bottom: 0; width: 8px; cursor: col-resize; background: rgba(27,42,74,0.15); border-radius: 2px; }
        .dash-resize-handle:hover { background: rgba(27,42,74,0.4); }
        .dash-audit-copy { background: none; border: none; color: var(--muted); cursor: pointer; padding: 2px; font-size: 13px; }
        @media (max-width: 700px) {
          .dash-audit-header { display: none; }
          .dash-audit-table { overflow-x: visible; }
          .dash-audit-row {
            display: grid !important;
            grid-template-columns: 1fr auto !important;
            grid-template-areas: "ts by" "order order" "action action";
            gap: 3px 8px;
            padding: 10px 34px 10px 12px;
            position: relative;
            white-space: normal;
          }
          .dash-audit-row .dash-col-ts { grid-area: ts; font-size: 10px; color: var(--muted); overflow: visible; }
          .dash-audit-row .dash-col-by { grid-area: by; font-size: 10px; color: var(--muted); text-align: right; overflow: visible; }
          .dash-audit-row .dash-col-order { grid-area: order; font-size: 10px; color: var(--muted); overflow: visible; }
          .dash-audit-row .dash-col-prod, .dash-audit-row .dash-col-name, .dash-audit-row .dash-col-phone { display: none; }
          .dash-audit-row .dash-col-action { grid-area: action; font-size: 12px; color: var(--navy); white-space: normal; overflow: visible; }
          .dash-audit-copy { position: absolute; top: 8px; right: 8px; }
        }
        @media (max-width: 600px) {
          .admin-shell { flex-direction: column; }
          .admin-sidebar { width: 100% !important; border-right: none; border-bottom: 0.5px solid var(--border); }
          .sidebar-header { display: none; }
          .sidebar-buttons { flex-direction: row; gap: 8px; }
          .sidebar-btn { flex: 1; justify-content: center; padding: 10px 4px; font-weight: 500; }
          .toolbar-control { width: 100%; }
          .dash-pill { color: #0F1A33; border-color: rgba(27,42,74,0.35); font-weight: 500; }
          .dash-pill i { color: #0F1A33; }
          .dash-pill.active { color: #fff; }
          .dash-pill.active i { color: #fff; }
          .dash-audit-row, .dash-audit-header { font-size: 11px; }
        }
      `}</style>
    </div>
  );
}